/*
 * Copyright (c) 2017-2024. Code réalisé par IpsoSenso.
 */

import { ConfirmationDialog, dataTableActions, FormInput, FormSelect, FormSwitch, getStyles, SpringDataTable } from 'isotope-client'
import React, { useContext, useEffect } from 'react'
import { withStyles } from '@material-ui/core'
import Button from '@material-ui/core/Button'
import Fab from '@material-ui/core/Fab'
import Add from '@material-ui/icons/Add'
import SearchIcon from '@material-ui/icons/Search'
import { FormattedMessage, useIntl } from 'react-intl'
import Paper from '@material-ui/core/Paper'
import Grid from '@material-ui/core/Grid'
import MenuItem from '@material-ui/core/MenuItem'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'
import { compose } from 'redux'
import { injectActions } from 'isotope-client/components/snackbar/services/snackbarInjector'
import NoResultFragment from 'isotope-client/components/layout/NoResultFragment'
import { addUser, disableUser, restoreUser, saveUser } from '../../common/user/userApi'
import { getRoles } from 'isotope-client/modules/roles/services/rolesApi'
import { PhidemDataContext } from '../../common/phidemData/PhidemDataContext'
import { Field, Form } from 'react-final-form'
import { SubmissionError } from 'redux-form'
import UserFormWrapper from './UserFormWrapper'
import { renderRoleWithCenterName } from './services/userUtils'
import { sortRolesByImportance } from '../../common/user/userUtils'

const INITIAL_VALUES = {
	query: '',
	profile: 0,
	disabled: false
}

const styles = theme => getStyles({
	filters: {
		padding: theme.spacing(2),
		marginBottom: 50
	},
	search: {
		display: 'flex',
		justifyContent: 'flex-end'
	},
	fab: {
		position: 'absolute',
		right: 18,
		marginTop: 23
	}
})

const UsersManagement = (
	{
		refreshTable,
		snackSuccess,
		snackError,
		classes
	}
) => {
	const [roles, setRoles] = React.useState([])
	const intl = useIntl()
	const { user: { selectedZone, isSuperAdmin }, zones } = useContext(PhidemDataContext)
	const [openConfirmationDialog, setOpenConfirmationDialog] = React.useState(false)
	const [openForm, setOpenForm] = React.useState(false)
	const [rowToUpdate, setRowToUpdate] = React.useState(undefined)

	useEffect(() => {
		const timeoutId = setTimeout(() => {
			setFilters({
				...filters,
				zoneId: selectedZone?.id || (zones.length > 0 ? zones[0].id : undefined)
			})
		}, 500)

		return () => clearTimeout(timeoutId)
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [zones])

	const [filters, setFilters] = React.useState({
		...INITIAL_VALUES,
		zoneId: selectedZone?.id || (zones.length > 0 ? zones[0].id : undefined)
	})

	React.useEffect(() => {
		getRoles()
			.then(data => setRoles(sortRolesByImportance(data)))
	}, [])

	const onSubmitSearch = (values) => {
		setFilters(values)
	}

	const refreshUserList = () => {
		refreshTable()
	}

	const handleOpenConfirmation = (user) => {
		setOpenConfirmationDialog(true)
		setRowToUpdate(user)
	}
	const handleCloseConfirmation = () => {
		setOpenConfirmationDialog(false)
		setRowToUpdate(undefined)
	}
	const changeUserStatus = () => {
		const promise = rowToUpdate.disabled ? restoreUser(rowToUpdate.id) : disableUser(rowToUpdate.id)

		promise
			.then(() => {
				refreshUserList()
				handleCloseConfirmation()
				snackSuccess({ id: 'users.actions.updateStatusSuccess' })
			})
			.catch(() => snackError({ id: 'users.actions.updateStatusError' }))
	}

	const handleCloseForm = () => {
		setOpenForm(false)
		setRowToUpdate(undefined)
	}
	const onSubmitUser = (values) => {
		const promise = values.id ? saveUser(values) : addUser(values)
		return promise
			.then(() => {
				snackSuccess({ id: `users.actions.${values.id ? 'update' : 'create'}Success` })
				handleCloseForm()
				refreshUserList()
			})
			.catch(e => {
				snackError({ id: 'global.errorForm' })
				throw new SubmissionError(e)
			})
	}

	const getHeaders = () => {
		const headers = [
			{
				key: 'lastname',
				name: <FormattedMessage
					id="users.lastname"
					defaultMessage="Nom"
					description="Header column name"
				/>,
				sortable: true
			},
			{
				key: 'firstname',
				name: <FormattedMessage
					id="users.firstname"
					defaultMessage="Prénom"
					description="Header column name"
				/>,
				sortable: true
			},
			{
				key: 'login',
				name: <FormattedMessage
					id="users.login"
					defaultMessage="Identifiant"
					description="Header column name"
				/>,
				sortable: true
			},
			{
				key: 'email',
				name: <FormattedMessage
					id="users.email"
					defaultMessage="Mail"
					description="Header column name"
				/>,
				sortable: true
			},
			{
				key: 'phoneNumber',
				name: <FormattedMessage
					id="users.phoneNumber"
					defaultMessage="Téléphone"
					description="Header column name"
				/>,
				sortable: true
			},
			{
				key: 'company',
				name: <FormattedMessage
					id="users.company"
					defaultMessage="Société"
					description="Header column name"
				/>,
				sortable: true
			},
			{
				key: 'profile',
				name: <FormattedMessage
					id="users.profile"
					defaultMessage="Profil"
					description="Header column name"
				/>,
				render: user => (<React.Fragment>
					{(user.roles || [])
						.map((role, index) => (
							<React.Fragment key={index}>
								{index > 0 && ', '}
								{renderRoleWithCenterName(role.code, intl, selectedZone)}
							</React.Fragment>
						))
					}
				</React.Fragment>)
			}
		]

		if (filters.disabled) {
			headers.push({
				key: 'disabled',
				name: <FormattedMessage
					id="users.status.header"
					defaultMessage="Statut"
					description="Header column name"
				/>,
				sortable: true,
				render: row => <FormattedMessage
					id={`users.status.${row.disabled ? 'inactive' : 'active'}`}
					defaultMessage="Statut"
					description="Status value"
				/>
			})
		}

		return headers
	}

	return (<>
			<Grid item container xs={6}>
				<Paper className={classes.filters}>
					<Form
						onSubmit={onSubmitSearch}
						initialValues={{
							...INITIAL_VALUES,
							zoneId: selectedZone?.id || (zones.length > 0 ? zones[0].id : '')
						}}
						render={({ handleSubmit }) => (
							<form onSubmit={handleSubmit} autoComplete="off">
								<Grid container spacing={2}>
									<Grid item container xs={12}>
										<Grid item xs={4}>
											<Field
												name="query"
												component={FormInput}
												label={
													<FormattedMessage
														id="users.query"
														defaultMessage="Recherche textuelle"
														description="Text search"
													/>
												}
											/>
										</Grid>
									</Grid>
									{isSuperAdmin && <Grid item xs={12} md={4}>
										<Field
											name="zoneId"
											component={FormSelect}
											label={
												<FormattedMessage
													id="product.Zone"
													defaultMessage="Zone"
													description="Zone"
												/>
											}
										>
											{zones.map((zone) => (
												<MenuItem key={zone.id} value={zone.id}>
													{zone.name}
												</MenuItem>
											))}
										</Field>
									</Grid>}
									<Grid item container xs={12}>
										<Grid item xs={4}>
											<Field
												name="profile"
												component={FormSelect}
												label={
													<FormattedMessage
														id="users.profile"
														defaultMessage="Profil"
														description="Profile"
													/>
												}
											>
												<MenuItem key="default" value={0}>
													<FormattedMessage
														id="users.selectAll"
														defaultMessage="Tous"
														description="All"
													/>
												</MenuItem>
												{roles.map(role =>
													<MenuItem key={`role-${role.code}`} value={role.code}>
														{renderRoleWithCenterName(role.code, intl, selectedZone)}
													</MenuItem>
												)}
											</Field>
										</Grid>
									</Grid>
									<Grid item xs={6}>
										<Field
											name="disabled"
											component={FormSwitch}
											type="checkbox"
											label={
												<FormattedMessage
													id="users.showDisabled"
													defaultMessage="Afficher les utilisateurs désactivés"
													description="Show users disabled or not"
												/>
											}
										/>
									</Grid>
									<Grid item xs={12} className={classes.search}>
										<Button
											type="submit"
											variant="contained"
											color="primary"
											startIcon={<SearchIcon />}
										>
											<FormattedMessage
												id="users.search"
												defaultMessage="Rechercher"
												description="Search"
											/>
										</Button>
									</Grid>
								</Grid>
							</form>
						)} />
				</Paper>
			</Grid>
			<SpringDataTable
				apiUrl="/utilisateurs/page"
				nom="usersList"
				headers={getHeaders()}
				filters={{
					...filters,
					profile: filters.profile !== 0 ? filters.profile : undefined
				}}
				noResultFragment={<NoResultFragment />}
				makeActions={user => [{
					label: 'Modifier', action: user => {
						setOpenForm(true)
						setRowToUpdate(user)
					}
				},
					{ label: user.disabled ? 'Restaurer' : 'Désactiver', action: user => handleOpenConfirmation(user) }
				]}
				showActions
			/>
			{openForm && <UserFormWrapper
				user={rowToUpdate}
				roles={roles}
				isEdition={!!rowToUpdate}
				onClose={handleCloseForm}
				onSubmit={onSubmitUser}
			/>}
			<ConfirmationDialog
				open={openConfirmationDialog}
				title={rowToUpdate && rowToUpdate.disabled ? 'Restauration de l\'utilisateur' : 'Désactivation de l\'utilisateur'}
				cancelAction={handleCloseConfirmation}
				confirmAction={changeUserStatus}
			/>
			<Fab
				color="primary"
				onClick={() => setOpenForm(true)}
				className={classes.fab}
			>
				<Add />
			</Fab>
		</>
	)
}

UsersManagement.propTypes = {
	handleSubmit: PropTypes.func.isRequired,
	refreshTable: PropTypes.func.isRequired,
	snackSuccess: PropTypes.func.isRequired,
	snackError: PropTypes.func.isRequired,
	classes: PropTypes.object
}

const actions = {
	refreshTable: () => dataTableActions.refresh('userList')
}

export default compose(
	connect(undefined, actions),
	injectActions,
	withStyles(styles)
)(UsersManagement)
